import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, of, switchMap, tap, throwError } from 'rxjs';
import { SettlementsService } from 'src/app/services/settlements.service';
import { AuthService } from 'src/app/shared/services/auth.service';

@Component({
  selector: 'app-reactivate-settlement',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <form [formGroup]="form" (ngSubmit)="reactivate()">
      <div class="modal-header">
        <h2 class="modal-title fs-5" id="reactivate-settlement-title">Reactivar cliente</h2>
        <button type="button" class="btn-close" aria-label="Cerrar" [disabled]="saving" (click)="modal.dismiss()"></button>
      </div>
      <div class="modal-body">
        @if (loading) {
          <p class="text-muted mb-0" role="status">Consultando la configuración anterior…</p>
        } @else if (loaded) {
          <p class="fw-bold">{{ name }}</p>
          <fieldset [disabled]="saving || !auth.canEditSettlements()">
            <label for="reactivate-contract-term" class="form-label">Temporalidad del contrato (años)</label>
            <input id="reactivate-contract-term" class="form-control" type="number" min="1" step="1"
              formControlName="contract_term_years" [attr.aria-invalid]="form.controls.contract_term_years.invalid"
              aria-describedby="reactivate-contract-error">
            @if (form.controls.contract_term_years.touched && form.controls.contract_term_years.invalid) {
              <p id="reactivate-contract-error" class="text-danger small mt-1">Indica un número entero de años mayor que cero.</p>
            }
            <div class="form-check mt-3">
              <input id="reactivate-automatic-renewal" class="form-check-input" type="checkbox" formControlName="automatic_renewal">
              <label for="reactivate-automatic-renewal" class="form-check-label">Renovación automática</label>
            </div>
          </fieldset>
        }
        @if (error) {
          <p class="text-danger mt-3 mb-0" role="alert">{{ error }}</p>
        }
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-link" [disabled]="saving" (click)="modal.dismiss()">Cancelar</button>
        <button type="submit" class="btn btn-zazz-dark"
          [disabled]="loading || !loaded || saving || form.invalid || !auth.canEditSettlements()">
          {{ saving ? 'Reactivando…' : 'Reactivar' }}
        </button>
      </div>
    </form>
  `
})
export class ReactivateSettlementComponent implements OnInit {
  @Input({ required: true }) settlementId!: number;
  readonly modal = inject(NgbActiveModal);
  readonly auth = inject(AuthService);
  private readonly store = inject(SettlementsService);
  private readonly destroyRef = inject(DestroyRef);
  readonly form = new FormGroup({
    contract_term_years: new FormControl<number | null>(null, [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]),
    automatic_renewal: new FormControl(false, { nonNullable: true })
  });
  name = '';
  loading = true;
  loaded = false;
  saving = false;
  error = '';
  private savedConfiguration: { contract_term_years: number; automatic_renewal: boolean } | null = null;

  ngOnInit(): void {
    if (!this.auth.canEditSettlements()) {
      this.loading = false;
      this.error = 'No tienes permiso para reactivar este cliente.';
      return;
    }
    this.store.getSettlement(this.settlementId).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading = false)
    ).subscribe({
      next: settlement => {
        if (settlement.enabled !== false) {
          this.error = 'El cliente ya no está archivado. Actualiza la lista.';
          return;
        }
        this.name = settlement.name;
        const years = Number(settlement.contract_term_years);
        const renewal = settlement.automatic_renewal;
        if (!Number.isInteger(years) || years < 1 || typeof renewal !== 'boolean') {
          this.error = 'No se pudo recuperar la configuración anterior del contrato.';
          return;
        }
        this.savedConfiguration = { contract_term_years: years, automatic_renewal: renewal };
        this.form.setValue(this.savedConfiguration);
        this.loaded = true;
      },
      error: () => this.error = 'No fue posible consultar el cliente. Cierra e intenta de nuevo.'
    });
  }

  reactivate(): void {
    this.form.markAllAsTouched();
    if (!this.loaded || this.loading || this.saving || this.form.invalid || !this.auth.canEditSettlements()) return;
    const configuration = {
      contract_term_years: Number(this.form.controls.contract_term_years.value),
      automatic_renewal: this.form.controls.automatic_renewal.value
    };
    const changed = configuration.contract_term_years !== this.savedConfiguration?.contract_term_years
      || configuration.automatic_renewal !== this.savedConfiguration?.automatic_renewal;
    this.saving = true;
    this.error = '';
    let configurationSaved = false;
    const save = changed
      ? this.store.updateSettlement(this.settlementId, configuration).pipe(
          switchMap(() => this.store.getSettlement(this.settlementId)),
          tap(settlement => {
            if (Number(settlement.contract_term_years) !== configuration.contract_term_years
              || settlement.automatic_renewal !== configuration.automatic_renewal) {
              throw new Error('La configuración guardada no coincide. El cliente no se ha reactivado.');
            }
            this.savedConfiguration = configuration;
            configurationSaved = true;
          })
        )
      : of(null);
    save.pipe(
      switchMap(() => this.auth.canEditSettlements()
        ? this.store.updateSettlementEnabled(this.settlementId, true)
        : throwError(() => new Error('Se revocó el permiso para reactivar.'))),
      finalize(() => this.saving = false)
    ).subscribe({
      next: () => this.modal.close(true),
      error: () => {
        this.error = configurationSaved
          ? 'La configuración se guardó, pero no se pudo confirmar la reactivación. Puedes reintentar.'
          : 'No fue posible completar la reactivación. Revisa la configuración e intenta de nuevo.';
      }
    });
  }
}
